import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { EditIcon } from '@/shared/assets/icons/edit-icon';
import { DrawerController } from '@/shared/components/ui/drawer-controller/drawer-controller';
import { Form } from '@/shared/components/ui/form/form';
import { TSubmittedData } from '@/shared/components/ui/form/form.types';
import { MenuItem } from '@/shared/components/ui/menu-item/menu-item';
import { usePermissionCheck } from '@/shared/features/permissions/hooks/use-permission-check/use-permission-check';
import {
  TUpdateUserInputSchema,
  TUpdateUserProps,
} from '@/shared/features/rbac/components/update-user/update-user.types';
import { updateUserInputSchema } from '@/shared/features/rbac/components/update-user/update-user.validation';
import { UpdateUserForm } from '@/shared/features/rbac/components/update-user-form/update-user-form';
import { useRbacConfig } from '@/shared/features/rbac/context/rbac-config-context';
import { useLoggedInUser } from '@/shared/hooks/use-logged-in-user/use-logged-in-user';
import { useUpdateUserMutation } from '@/shared/redux/api/ident/ident-api';
import { showToastError } from '@/shared/utils/toast';

export const UpdateUser: React.FC<TUpdateUserProps> = ({ user, onClose }) => {
  const { t } = useTranslation();
  const [updateUser] = useUpdateUserMutation();
  const { rbacPermissionChecks } = useRbacConfig();
  const { can } = usePermissionCheck();
  const { isLoggedInUser } = useLoggedInUser();
  // The user can always edit their own basic profile (e.g. name); changing the
  // group still requires the updateUser permission. Editing other users
  // requires the permission outright.
  const canUpdate = can('backofficeUser:updateUser', user.userId);
  const isSelf = isLoggedInUser(user.userId);

  const handleSubmit = async (
    data: TSubmittedData<TUpdateUserInputSchema>,
    handleClose: () => void,
  ) => {
    try {
      await updateUser({
        userId: user.userId,
        user: { ...data.dirtyData, objectVersion: user!.objectVersion! + 1 },
      }).unwrap();
      toast.success(t('rbac:update_user_success'));
      handleClose();
      onClose?.();
    } catch {
      showToastError(t('rbac:update_user_error'));
    }
  };

  if (!isSelf && !canUpdate) {
    return null;
  }

  return (
    <DrawerController
      trigger={<MenuItem icon={<EditIcon />}>{t('rbac:update_user')}</MenuItem>}
      title={t('rbac:update_user')}
      showDivider={false}
      width="500px"
      permission={rbacPermissionChecks.updateUser?.(user)}
    >
      {(drawerClose) => {
        const handleClose = () => {
          drawerClose();
          onClose?.();
        };
        return (
          <Form<TUpdateUserInputSchema>
            onSubmit={(event) => handleSubmit(event, handleClose)}
            schema={updateUserInputSchema}
            defaultValues={{
              ...user,
            }}
          >
            {() => (
              <UpdateUserForm
                handleClose={handleClose}
                userSource={user.source}
                canEditGroup={canUpdate}
              />
            )}
          </Form>
        );
      }}
    </DrawerController>
  );
};
